import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Form, Button, Alert } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import "../styles/PdfPreviewModal.css";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { base64Encode } from '../utils/PreviewUtils'
import PropTypes from "prop-types";


const Legend = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  background: transparent;
  padding: 10px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  span {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 8px;
    background-color: ${({ color }) => color};
  }
`;


const PdfPreviewModal = ({
  isModalOpen,
  handleClose,
  pdfImageSrc,
  selectedTemplate,
  handleDownloadPdf,
  error,
  successMessage,
  templateName,
  setTemplateName,
  handleTemplateSave,
  isDesktop,
  templateType,
  templateID
}) => {
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [zoomScale, setZoomScale] = useState(1.0);
  const [zoomInput, setZoomInput] = useState("100%");
  const [baseWidthPx, setBaseWidthPx] = useState(null);
  const [tooltipText, setTooltipText] = useState("Copy URL");


  const calculateBaseFromFitMode = () => {
    const image = document.querySelector(".pdf-image-fit");

    if (image) {
      const imageWidth = image.getBoundingClientRect().width;
      if (imageWidth > 0) {
        return imageWidth;
      }
    }
    console.log("❌ Elements not found");
    return null;
  }

  const updateZoomAndBase = (newScale) => {
    if (zoomScale <= 1.0 && newScale > 1.0 && baseWidthPx === null) {
      const calculated = calculateBaseFromFitMode();
      if (calculated) {
        setBaseWidthPx(calculated);
      }
    } else if (newScale <= 1.0 && baseWidthPx !== null) {
      setBaseWidthPx(null);
    }
    setZoomScale(newScale);
  };

  useEffect(() => {
    setZoomInput(zoomScale === 1.0 ? "100%" : `${Math.round(zoomScale * 100)}%`);
  }, [zoomScale]);

  const handleZoomIn = () => {
    const currentPct = Math.round(zoomScale * 100);
    const nextPct = (Math.floor(currentPct / 5) + 1) * 5;
    updateZoomAndBase(Math.min(nextPct / 100, 3));
  };

  const handleZoomOut = () => {
    const currentPct = Math.round(zoomScale * 100);
    const nextPct = (Math.ceil(currentPct / 5) - 1) * 5;
    updateZoomAndBase(Math.max(nextPct / 100, 0.25));
  };

  const handleResetZoom = () => updateZoomAndBase(1.0);

  const handleZoomInputChange = (e) => {
    const value = e.target.value;
    if (/^[0-9.%]*$/.test(value)) {
      setZoomInput(value);
    }
  };

  const applyZoom = () => {
    const numericValue = parseFloat(zoomInput.replace('%', ''));
    if (numericValue > 0) {
      const newScale = Math.min(Math.max(numericValue / 100, 0.25), 3);
      updateZoomAndBase(newScale);
    } else {
      setZoomInput(zoomScale === 1.0 ? "100%" : `${Math.round(zoomScale * 100)}%`);
    }
  };

  const handleZoomKeyDown = (e) => {
    if (e.key === 'Enter') {
      applyZoom();
      e.target.blur();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateZoomAndBase(Math.min(zoomScale + 0.01, 3));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateZoomAndBase(Math.max(zoomScale - 0.01, 0.25));
    }
  };

  const isFitMode = zoomScale <= 1.0;


  const handleTemplateCopy = () => {
    const domainURL = process.env.REACT_APP_REDIRECT_URI;
    const encodedTemplateId = base64Encode(templateID)
    const finalURL = `${domainURL}/proof_templates?t=${encodedTemplateId}`
    try {
      navigator.clipboard.writeText(finalURL);
      setTooltipText("Copied");

      setTimeout(() => {
        setTooltipText("Copy URL");
      }, 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }

  // ── Shared header content (legends + save/download) ──
  const modalHeader = (
    <div className="modal-top-content-section">
      {templateType !== 'Postcard' && (
        <div className="modal-top-legends">
          <Legend style={{ padding: '0', gap: '15px' }}>
            <LegendItem color="#9400d3" style={{ fontSize: '12px', fontWeight: 600 }}><span></span>Variable</LegendItem>
            <LegendItem color="brown" style={{ fontSize: '12px', fontWeight: 600 }}><span></span>Fixed</LegendItem>
          </Legend>
        </div>
      )}
      {!showSaveForm ? (
        <div className="action-buttons">
          <Tooltip title={tooltipText} arrow placement="top">
            <Button
              onClick={handleTemplateCopy}
              style={{ cursor: "pointer", display: "inline-flex", gap: '3px' }}
              className="primary-button"
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>

          <Button className="primary-button" style={{ gap: '3px' }} onClick={() => setShowSaveForm(true)}>
            <SaveIcon sx={{ fontSize: 16 }} />
            <span className="preview-btn-text">Save</span>
          </Button>
          <Button onClick={handleDownloadPdf} className="primary-button" style={{ gap: '2px' }}>
            <DownloadIcon sx={{ fontSize: 16 }} />
            <span className="preview-btn-text">Download</span>
          </Button>
        </div>

      ) : (
        <div className="save-form-container">
          <div className="save-form-row">
            <Form.Group controlId="templateName" style={{ flex: 1, marginBottom: 0 }}>
              <Form.Control
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template Name"
                className="template-input"
              />
            </Form.Group>
            <div className="save-form-actions">
              <Button variant="danger" onClick={() => setShowSaveForm(false)} className="cancel-button">Cancel</Button>
              <Button onClick={handleTemplateSave} className="confirm-button">Confirm</Button>
            </div>
          </div>
        </div>
      )
      }
      {error && <Alert variant="danger" className="alert-error" style={{ width: '100%', margin: '10px 0 5px' }}>{error}</Alert>}
      {successMessage && <Alert variant="success" className="alert-success" style={{ width: '100%', margin: '10px 0 5px' }}>{successMessage}</Alert>}
    </div >
  );

  // ── PDF frame (shared between desktop and mobile) ──
  const pdfContent = (
    <div className="pdf-display-frame">
      {pdfImageSrc ? (
        <div className="zoom-wrapper">
          <div className="zoom-controls">
            <IconButton onClick={handleZoomOut} size="small" title="Zoom Out" className="zoom-btn">
              <RemoveIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <input
              type="text"
              className="zoom-percentage-input"
              value={zoomInput}
              onChange={handleZoomInputChange}
              onBlur={applyZoom}
              onKeyDown={handleZoomKeyDown}
            />
            <IconButton onClick={handleZoomIn} size="small" title="Zoom In" className="zoom-btn">
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton onClick={handleResetZoom} size="small" title="Reset Zoom" className="zoom-btn">
              <RestartAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>

          {isFitMode ? (
            <div
              className="pdf-fit-area"
              style={{
                transform: zoomScale < 1.0 ? `scale(${zoomScale})` : 'none',
                transformOrigin: 'center',
                transition: 'transform 0.15s ease-out'
              }}
            >
              {(Array.isArray(pdfImageSrc) ? pdfImageSrc : [pdfImageSrc]).map((src, index) => (
                <img
                  key={index}
                  src={src}
                  className="pdf-image-fit"
                  alt={`PDF page ${index + 1}`}
                />
              ))}
            </div>
          ) : (
            <div
              className="pdf-scroll-area"
              style={{ display: 'block', textAlign: 'center', padding: '5px' }}
            >
              <div
                className="pdf-image-container"
                style={{
                  width: baseWidthPx ? `${baseWidthPx * zoomScale}px` : `${100 * zoomScale}%`,
                  display: 'inline-flex',
                  flexDirection: 'column',
                  gap: '8px',
                  transition: 'all 0.1s ease-out'
                }}
              >
                {(Array.isArray(pdfImageSrc) ? pdfImageSrc : [pdfImageSrc]).map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    className="pdf-image"
                    alt={`PDF page ${index + 1}`}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="loading-container">
          <Typography className="loading-text">Loading preview...</Typography>
        </div>
      )}
    </div>
  );

  // ── DESKTOP: plain div with header + PDF, no MUI Modal ──
  if (isDesktop) {
    return (
      <div className="desktop-pdf-header">
        {modalHeader}
        <div className="modal-container-desktop">
          {pdfContent}
        </div>
      </div>
    );
  }

  // ── MOBILE: full MUI Modal ──
  return (
    <Modal
      open={isModalOpen}
      aria-labelledby="pdf-preview-modal"
      aria-describedby="pdf-preview-description"
      onClose={handleClose}
    >
      <Box
        className="modal-container-mobile"
        sx={{ background: 'white' }}
      >
        <div className="modal-header-section">
          <Typography
            id="pdf-preview-modal"
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '0.4rem', sm: '0.85rem', md: '0.95rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}
          >
            {selectedTemplate.split('|')[1]}
          </Typography>
          <IconButton
            onClick={handleClose}
            className="modal-close-icon-btn"
            sx={{
              transition: 'all 0.2s',
              p: { xs: 0.3, sm: 0.8 },
              color: '#1e293b',
              flexShrink: 0,
              marginLeft: '8px'
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 12, sm: 16, md: 18 } }} />
          </IconButton>
        </div>


        {modalHeader}
        {pdfContent}
      </Box>
    </Modal>
  );

};

export default PdfPreviewModal;

PdfPreviewModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  pdfImageSrc: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  selectedTemplate: PropTypes.string,
  handleDownloadPdf: PropTypes.func.isRequired,
  error: PropTypes.string,
  successMessage: PropTypes.string,
  templateName: PropTypes.string,
  setTemplateName: PropTypes.func.isRequired,
  handleTemplateSave: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  templateType: PropTypes.string,
};